import { useTranslation } from 'react-i18next'
import { useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { SECTION_ICONS } from '../../lib/section-icons'
import { useKnowledgeProposal, useKnowledgeProposals } from '../../hooks/useKnowledge'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { EmptyState } from '../ui/empty-state'
import { formatRelativeTime } from '../PageVersionHistory'
import { KindBadge } from './KindBadge'
import { KnowledgeProposalReviewDialog, ProposalNoteLink } from './KnowledgeProposalDialog'

const PENDING = 'pending,stale'
const DECIDED = 'approved,rejected,dismissed,withdrawn'

/**
 * The Proposals tab of `/$org/knowledge` (spec 2026-11-01-01): the proposals
 * the caller may decide plus their own, each opening in review mode. The open
 * proposal lives in the URL (`?tab=proposals&proposal=<uid>`), which is the
 * link the assistant answers with.
 */
export function KnowledgeProposalsTab({ org, proposalUid }: { org: string; proposalUid?: string }) {
    const { t } = useTranslation('knowledge')
    const navigate = useNavigate()
    const [filter, setFilter] = useState<'pending' | 'decided'>('pending')
    const { data: proposals, isLoading, error } = useKnowledgeProposals(org, filter === 'pending' ? PENDING : DECIDED)
    const fromList = proposals?.find((p) => p.uid === proposalUid)
    // A link can name a proposal outside the list shown (decided, filtered).
    const { data: fetched } = useKnowledgeProposal(org, proposalUid && !fromList && !isLoading ? proposalUid : undefined)
    const open = fromList ?? fetched ?? null

    const select = (uid?: string) =>
        navigate({
            to: '/$org/knowledge',
            params: { org },
            search: (prev) => ({ ...prev, tab: 'proposals' as const, proposal: uid }),
        })

    return (
        <div className="space-y-3" data-testid="knowledge-proposals">
            <div className="flex flex-wrap gap-1" role="tablist">
                {(['pending', 'decided'] as const).map((f) => (
                    <Button
                        key={f}
                        role="tab"
                        aria-selected={filter === f}
                        variant={filter === f ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setFilter(f)}
                        data-testid={`knowledge-proposals-filter-${f}`}
                    >
                        {t(`proposals.filter_${f}`)}
                    </Button>
                ))}
            </div>
            {isLoading ? (
                <p className="text-sm text-muted-foreground">{t('loading')}</p>
            ) : error ? (
                <p className="text-destructive" role="alert">{t('proposals.load_failed')}</p>
            ) : (proposals ?? []).length === 0 ? (
                <EmptyState
                    icon={SECTION_ICONS.knowledge}
                    title={t('proposals.empty')}
                    description={t('proposals.empty_description')}
                    data-testid="knowledge-proposals-empty"
                />
            ) : (
                <ul className="space-y-2" data-testid="knowledge-proposal-list">
                    {(proposals ?? []).map((p) => (
                        <li
                            key={p.uid}
                            className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-card p-3"
                            data-testid="knowledge-proposal-item"
                            data-proposal-uid={p.uid}
                        >
                            <div className="min-w-0 space-y-1">
                                <div className="flex flex-wrap items-center gap-2">
                                    <Badge variant={p.action === 'retire' ? 'destructive' : 'secondary'}>{t(`proposals.action.${p.action}`)}</Badge>
                                    <span className="font-medium text-foreground" data-testid="knowledge-proposal-item-title">
                                        {p.title}
                                    </span>
                                    <KindBadge kind={p.kind} />
                                    {p.status !== 'pending' && (
                                        <span className="text-xs text-muted-foreground" data-testid="knowledge-proposal-item-status">
                                            {t(`proposals.status.${p.status}`)}
                                        </span>
                                    )}
                                </div>
                                <div className="flex flex-wrap gap-x-3 text-xs text-muted-foreground">
                                    <span>
                                        {p.target.type === 'organization'
                                            ? t('proposals.target_organization')
                                            : t('proposals.target_asset', { name: p.target.asset_name ?? '' })}
                                    </span>
                                    <span>{t(`proposals.origin.${p.origin}`)}</span>
                                    {p.mine ? (
                                        <span>{t('proposals.mine')}</span>
                                    ) : (
                                        p.proposed_by_name && <span>{t('proposals.by', { name: p.proposed_by_name })}</span>
                                    )}
                                    <span>{formatRelativeTime(p.created_at)}</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <ProposalNoteLink org={org} proposal={p} />
                                <Button variant="outline" size="sm" onClick={() => void select(p.uid)} data-testid="knowledge-proposal-open">
                                    {t('proposals.open')}
                                </Button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
            <KnowledgeProposalReviewDialog org={org} proposal={open} onClose={() => void select(undefined)} />
        </div>
    )
}
